import { c as _c } from "react/compiler-runtime";
import { Minus, Plus } from 'lucide-react';
import { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from '@/hooks/use-translation';
import { decimalSeparatorOf } from '@/lib/format/format-weight';
import { Stepper } from '../stepper/Stepper';
export interface NumericSelectorProps {
  value: number;
  onChange: (value: number) => void;
  labelFormatter?: (value: number) => string;
  minValue?: number;
  maxValue?: number;
  step?: number;
  maxCharacters?: number;
}
const getDecimalPlaces = (step: number): number => {
  const stepStr = step.toString();
  const decimalIndex = stepStr.indexOf('.');
  return decimalIndex === -1 ? 0 : stepStr.length - decimalIndex - 1;
};
const roundToStepPrecision = (value: number, step: number): number => {
  const decimalPlaces = getDecimalPlaces(step);
  return Math.round(value * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
};

/**
 * What the reader typed, as a number — or NaN unless all of it is one.
 *
 * A French reader types a comma, since that is what the label shows them;
 * `parseFloat` would stop at it and quietly keep the whole part, 9,7 becoming
 * 9. And a value with anything after it is refused rather than cut short.
 */
const parseTypedNumber = (typed: string): number => {
  const normalised = typed.replaceAll(',', '.').replace(/\s/g, '');
  return normalised === '' ? NaN : Number(normalised);
};
const calculateMaxCharacters = (maxValue: number, step: number): number => {
  const decimalPlaces = getDecimalPlaces(step);
  const maxValueStr = maxValue.toFixed(decimalPlaces);
  return maxValueStr.length;
};
export const NumericSelector = t0 => {
  const $ = _c(70);
  const {
    value,
    onChange,
    minValue,
    maxValue,
    step: t1,
    labelFormatter,
    maxCharacters
  } = t0;
  const step = t1 === undefined ? 1 : t1;
  const {
    currentLanguage,
    t
  } = useTranslation();
  let t2;
  if ($[0] !== currentLanguage) {
    t2 = decimalSeparatorOf(currentLanguage);
    $[0] = currentLanguage;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const decimalSeparator = t2;
  let t3;
  if ($[2] !== decimalSeparator) {
    t3 = number => number.toString().replace(".", decimalSeparator);
    $[2] = decimalSeparator;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const asTyped = t3;
  let t4;
  if ($[4] !== asTyped || $[5] !== value) {
    t4 = value === undefined ? "" : asTyped(value);
    $[4] = asTyped;
    $[5] = value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const valueString = t4;
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(valueString);
  const inputRef = useRef(null);
  let t5;
  bb0: {
    if (maxCharacters !== undefined) {
      t5 = maxCharacters;
      break bb0;
    }
    if (maxValue !== undefined) {
      let t6;
      if ($[7] !== maxValue || $[8] !== step) {
        t6 = calculateMaxCharacters(maxValue, step);
        $[7] = maxValue;
        $[8] = step;
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t5 = t6;
      break bb0;
    }
    t5 = undefined;
  }
  const effectiveMaxCharacters = t5;
  let t6;
  if ($[10] !== labelFormatter) {
    t6 = value_0 => labelFormatter ? labelFormatter(value_0) : value_0?.toString() ?? "";
    $[10] = labelFormatter;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const formatLabel = t6;
  let t7;
  let t8;
  if ($[12] !== isEditing) {
    t7 = () => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    };
    t8 = [isEditing];
    $[12] = isEditing;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[15] !== valueString) {
    t9 = () => {
      setInputValue(valueString);
    };
    t10 = [valueString];
    $[15] = valueString;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  const canIncrement = value + step <= (maxValue ?? Infinity);
  let t11;
  if ($[18] !== canIncrement || $[19] !== onChange || $[20] !== step || $[21] !== value) {
    t11 = () => {
      if (!canIncrement) {
        return;
      }
      onChange(roundToStepPrecision(value + step, step));
    };
    $[18] = canIncrement;
    $[19] = onChange;
    $[20] = step;
    $[21] = value;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const handleIncrement = t11;
  const canDecrement = value - step >= (minValue ?? -Infinity);
  let t12;
  if ($[23] !== canDecrement || $[24] !== onChange || $[25] !== step || $[26] !== value) {
    t12 = () => {
      if (!canDecrement) {
        return;
      }
      onChange(roundToStepPrecision(value - step, step));
    };
    $[23] = canDecrement;
    $[24] = onChange;
    $[25] = step;
    $[26] = value;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const handleDecrement = t12;
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setIsEditing(true);
    };
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const handleValueClick = t13;
  let t14;
  if ($[29] !== asTyped || $[30] !== maxValue || $[31] !== minValue || $[32] !== onChange || $[33] !== step || $[34] !== value || $[35] !== valueString) {
    t14 = inputVal => {
      const numValue = parseTypedNumber(inputVal);
      if (isNaN(numValue)) {
        setInputValue(valueString);
        return;
      }
      const roundedValue = roundToStepPrecision(numValue, step);
      if (roundedValue < (minValue ?? -Infinity) || roundedValue > (maxValue ?? Infinity)) {
        setInputValue(valueString);
        return;
      }
      if (roundedValue !== value) {
        onChange(roundedValue);
      }
      setInputValue(asTyped(roundedValue));
    };
    $[29] = asTyped;
    $[30] = maxValue;
    $[31] = minValue;
    $[32] = onChange;
    $[33] = step;
    $[34] = value;
    $[35] = valueString;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const validateAndApplyValue = t14;
  let t15;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e => {
      setInputValue(e.target.value);
    };
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const handleInputChange = t15;
  let t16;
  if ($[38] !== inputValue || $[39] !== validateAndApplyValue || $[40] !== valueString) {
    t16 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        validateAndApplyValue(inputValue);
        setIsEditing(false);
      } else {
        if (e_0.key === "Escape") {
          e_0.preventDefault();
          setInputValue(valueString);
          setIsEditing(false);
        }
      }
    };
    $[38] = inputValue;
    $[39] = validateAndApplyValue;
    $[40] = valueString;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const handleInputKeyDown = t16;
  let t17;
  if ($[42] !== inputValue || $[43] !== validateAndApplyValue) {
    t17 = () => {
      validateAndApplyValue(inputValue);
      setIsEditing(false);
    };
    $[42] = inputValue;
    $[43] = validateAndApplyValue;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const handleInputBlur = t17;
  const valueWidth = effectiveMaxCharacters ? `calc(${effectiveMaxCharacters}ch + 2.5rem)` : undefined;
  let t18;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Plus className="size-[17px]" />;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t) {
    t19 = t("Increase");
    $[46] = t;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Minus className="size-[17px]" />;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t) {
    t21 = t("Decrease");
    $[49] = t;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== formatLabel || $[52] !== handleInputBlur || $[53] !== handleInputKeyDown || $[54] !== inputValue || $[55] !== isEditing || $[56] !== maxValue || $[57] !== minValue || $[58] !== step || $[59] !== value || $[60] !== valueWidth) {
    t22 = isEditing ? <input ref={inputRef} value={inputValue} onChange={handleInputChange} onKeyDown={handleInputKeyDown} onBlur={handleInputBlur} className="h-10 min-w-16 bg-transparent px-2 text-center font-mono text-base font-medium outline-none" min={minValue} max={maxValue} step={step} size={inputValue.length || 1} style={{
      width: valueWidth
    }} /> : <button type="button" className="h-10 min-w-16 cursor-text px-2 font-mono text-base font-medium transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset focus-visible:outline-none" onClick={handleValueClick} style={{
      width: valueWidth
    }}>{formatLabel(value)}</button>;
    $[51] = formatLabel;
    $[52] = handleInputBlur;
    $[53] = handleInputKeyDown;
    $[54] = inputValue;
    $[55] = isEditing;
    $[56] = maxValue;
    $[57] = minValue;
    $[58] = step;
    $[59] = value;
    $[60] = valueWidth;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== canDecrement || $[63] !== canIncrement || $[64] !== handleDecrement || $[65] !== handleIncrement || $[66] !== t19 || $[67] !== t21 || $[68] !== t22) {
    t23 = <Stepper canGoNext={canIncrement} canGoPrevious={canDecrement} nextIcon={t18} nextLabel={t19} onNext={handleIncrement} onPrevious={handleDecrement} previousIcon={t20} previousLabel={t21}>{t22}</Stepper>;
    $[62] = canDecrement;
    $[63] = canIncrement;
    $[64] = handleDecrement;
    $[65] = handleIncrement;
    $[66] = t19;
    $[67] = t21;
    $[68] = t22;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  return t23;
};